import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Paper, Chip, Avatar, Divider, CircularProgress, Alert,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import PhoneCallbackRounded from '@mui/icons-material/PhoneCallbackRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import { alpha } from '@mui/material/styles';
import { getContactRemarks, addContactRemark } from '../../services/studentService';

const CONTACT_STATUSES = [
  { value: 'contacted', label: 'Contacted', color: '#6366F1' },
  { value: 'interested', label: 'Interested', color: '#10B981' },
  { value: 'not_interested', label: 'Not Interested', color: '#EF4444' },
  { value: 'follow_up', label: 'Follow Up', color: '#F59E0B' },
  { value: 'no_response', label: 'No Response', color: '#6B7280' },
];

const getStatusConfig = (status) =>
  CONTACT_STATUSES.find((s) => s.value === status) || { label: status, color: '#6B7280' };

const formatDateTime = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) + ' at ' + d.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const ContactRemarks = ({ studentId }) => {
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState('');

  // Form fields
  const [contactStatus, setContactStatus] = useState('');
  const [remarkText, setRemarkText] = useState('');

  const fetchRemarks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getContactRemarks(studentId);
      setRemarks(res.data?.data?.contactRemarks || []);
    } catch (err) {
      console.error('Failed to fetch remarks:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchRemarks();
  }, [fetchRemarks]);

  const handleSubmit = async () => {
    if (!contactStatus || !remarkText.trim()) {
      setFormError('Please select a status and enter remarks');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      const res = await addContactRemark(studentId, {
        contactStatus,
        remarks: remarkText.trim(),
      });
      setRemarks(
        [...(res.data?.data?.contactRemarks || [])].sort(
          (a, b) => new Date(b.contactedAt) - new Date(a.contactedAt)
        )
      );
      setContactStatus('');
      setRemarkText('');
      setShowForm(false);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to add remark');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneCallbackRounded sx={{ color: '#6366F1', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Contact Remarks
          </Typography>
          <Chip
            label={remarks.length}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 700,
              backgroundColor: alpha('#6366F1', 0.1),
              color: '#6366F1',
            }}
          />
        </Box>
        <Button
          variant={showForm ? 'outlined' : 'contained'}
          size="small"
          startIcon={<AddRounded />}
          onClick={() => setShowForm(!showForm)}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
        >
          {showForm ? 'Cancel' : 'Add Remark'}
        </Button>
      </Box>

      {/* Add Remark Form */}
      {showForm && (
        <Paper
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: alpha('#6366F1', 0.2),
            backgroundColor: alpha('#6366F1', 0.02),
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 2 }}>
            New Contact Entry
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Contact Status</InputLabel>
              <Select
                value={contactStatus}
                label="Contact Status"
                onChange={(e) => setContactStatus(e.target.value)}
              >
                {CONTACT_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: s.color,
                        }}
                      />
                      {s.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Remarks"
            placeholder="Enter details about the contact..."
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            sx={{ mb: 2 }}
          />

          {formError && (
            <Alert severity="error" sx={{ mb: 2, py: 0 }}>
              {formError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setShowForm(false); setFormError(''); }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {submitting ? 'Saving...' : 'Save Remark'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Remarks Timeline */}
      {remarks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}>
          <PhoneCallbackRounded sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
          <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
            No contact remarks yet. Add the first one!
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {/* Timeline line */}
          <Box
            sx={{
              position: 'absolute',
              left: 15,
              top: 8,
              bottom: 8,
              width: 2,
              backgroundColor: '#E5E7EB',
              borderRadius: 1,
            }}
          />

          {remarks.map((remark, idx) => {
            const config = getStatusConfig(remark.contactStatus);
            return (
              <Box
                key={remark._id || idx}
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  position: 'relative',
                }}
              >
                {/* Timeline dot */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: alpha(config.color, 0.12),
                    border: `2px solid ${config.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: config.color,
                    }}
                  />
                </Box>

                {/* Card */}
                <Paper
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    transition: 'border-color 0.15s ease',
                    '&:hover': { borderColor: alpha(config.color, 0.3) },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: config.color,
                        backgroundColor: alpha(config.color, 0.1),
                        border: `1px solid ${alpha(config.color, 0.2)}`,
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeRounded sx={{ fontSize: 14, color: '#9CA3AF' }} />
                      <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                        {formatDateTime(remark.contactedAt)}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: '0.84rem', color: '#374151', lineHeight: 1.6, mb: 1 }}>
                    {remark.remarks}
                  </Typography>

                  <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                    by {remark.contactedBy?.name || 'Unknown'}
                  </Typography>
                </Paper>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ContactRemarks;
